import { Flex, Grid, Box, Spinner, IconButton, Tooltip } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import Card from './Card'
import { useGetDevicesDataQuery } from '../api/automationsApi'
import NotFound from './NotFound'
import { useInView } from 'react-intersection-observer'
import { AddIcon } from '@chakra-ui/icons'
import { useDeviceStatusContext } from '../../../context/DeviceStatusContext'
import { findDeviceBrightness, findDeviceOutput } from '../../../utils/helpers'

interface DeviceProps {
    querySearchParams: any
    deviceId: number
    groupId: number
    locationId: number
    searchInput: string
    hasAutomation?: boolean
    handleOpenModal: (device: any, param: any) => void
}

export default function Device({
    querySearchParams,
    deviceId,
    groupId,
    locationId,
    searchInput,
    hasAutomation,
    handleOpenModal,
}: DeviceProps) {
    const [page, setPage] = useState(1)
    const [allDevices, setAllDevices] = useState<any[]>([])
    const [brightnessMap, setBrightnessMap] = useState<Record<number, number>>({})
    const { deviceStatusData } = useDeviceStatusContext()

    const { data, isLoading, isFetching } = useGetDevicesDataQuery({
        groups: groupId,
        locations: locationId,
        devices: deviceId,
        search: searchInput,
        has_automation: hasAutomation,
        page: searchInput || groupId || locationId || deviceId ? 1 : page,
    })

    const { ref, inView, entry } = useInView({
        threshold: 1.0,
    })
    useEffect(() => {
        setPage(1)
        setAllDevices([])
    }, [searchInput, groupId, locationId, deviceId, hasAutomation])

    useEffect(() => {
        if (inView && entry.isIntersecting && !isFetching && data?.next) {
            setPage((prev) => prev + 1)
        }
    }, [inView, entry, isFetching, data?.next])

    useEffect(() => {
        if (data?.results) {
            setAllDevices((prev) => {
                return data.results.reduce((acc, incomingDevice) => {
                    const existingIndex = acc.findIndex((d) => d.id === incomingDevice.id)

                    if (existingIndex !== -1) {
                        const existingDevice = acc[existingIndex]

                        if (existingDevice.active_automations !== incomingDevice.active_automations || existingDevice.inactive_automations !== incomingDevice.inactive_automations) {
                            acc[existingIndex] = incomingDevice
                        }
                    } else {
                        acc.push(incomingDevice)
                    }

                    return [...acc]
                }, [...prev])
            })
        }
    }, [data])

    if ((isLoading || isFetching) && page === 1) {
        return (
            <Flex justifyContent="center" alignItems="center" height="200px">
                <Spinner size="xl" />
            </Flex>
        )
    }

    if (allDevices.length === 0) {
        return (
            <NotFound
                title="No smart devices"
                description="Currently there are no devices that support automation and smart control"
                header={false}
            />
        )
    }


    return (
        <Grid templateColumns="repeat(4, 1fr)" gap={4}>
            {allDevices.flatMap((device) =>
                device.model_channel_topics.map((param: any) => (

                    <Card
                        key={`${device.id}-${param.id}`}
                        title={`${device.title} - ${param.description || param.title}`}
                        id={param.id}
                        deviceId={device.id}
                        parameter={param.title}
                        toggle={findDeviceOutput(device.id, param.title, deviceStatusData)}
                        brightness={brightnessMap[param.id] ?? findDeviceBrightness(device.id, param.title, deviceStatusData)}
                        onBrightnessChange={(value) => setBrightnessMap((prev) => ({
                            ...prev,
                            [param.id]: value,
                        }))}
                        activeAutomations={device.active_automations}
                        inactiveAutomations={device.inactive_automations}
                        chanelTopicId = {param.id}
                        actions={
                            <Tooltip placement="top" label="New automation" hasArrow offset={[0, -5]}>
                                <IconButton
                                    icon={<AddIcon />}
                                    aria-label="Settings"
                                    variant="unstyled"
                                    color="blue.500"
                                    size="lg"
                                    position="absolute"
                                    top="-1"
                                    right="0"
                                    zIndex="1"
                                    _focus={{ boxShadow: 'none' }}
                                    _focusVisible={{ boxShadow: 'none' }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleOpenModal(device, { id: param.id, title: param.title, description: param.description })
                                    }}
                                />
                            </Tooltip>
                        }
                    />
                ))
            )}

            {/* Infinite scroll sentinel */}
            <Box
                ref={ref}
                height="40px"
                gridColumn="1 / -1"
                display="flex"
                justifyContent="center"
            >
                {isFetching && <Spinner />}
            </Box>
        </Grid>
    )
}
